export interface RetryPolicy {
  readonly maxAttempts: number
  readonly baseDelayMs: number
}

export class RetriesExhaustedError extends Error {
  constructor(
    public readonly attempts: number,
    public readonly lastError: unknown
  ) {
    super(lastError instanceof Error ? lastError.message : String(lastError))
    this.name = "RetriesExhaustedError"
  }
}

const BACKOFF_FACTOR = 2
const FIRST_ATTEMPT = 1

const wait = async (delayMs: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, delayMs))
}

const attemptOperation = async <T>(
  operation: () => Promise<T>,
  policy: RetryPolicy,
  attempt: number
): Promise<T> => {
  try {
    return await operation()
  } catch (error) {
    if (attempt >= policy.maxAttempts) {
      throw new RetriesExhaustedError(attempt, error)
    }
    await wait(policy.baseDelayMs * BACKOFF_FACTOR ** (attempt - FIRST_ATTEMPT))
    return await attemptOperation(operation, policy, attempt + 1)
  }
}

/** Runs `operation` up to `policy.maxAttempts` times, doubling the wait from `policy.baseDelayMs` after each
 * failure, then rejects with {@link RetriesExhaustedError} carrying the attempt count and the last failure. */
export const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  policy: RetryPolicy
): Promise<T> => await attemptOperation(operation, policy, FIRST_ATTEMPT)
